var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, createContext, useRef, useContext, useMemo } from "react";
import { shouldShowAppUpdatePrompt } from "./util/shouldShowAppUpdatePrompt";
// We might want to make this configurable, but for now if a user dismisses an app update, reprompt them after a day
const DISMISS_TIME_MS = 24 * 60 * 60 * 1000;
// If we're doing multiple updates (often we'll do an update and have to quickly fix a defect, or realise there was
// another update to go out we missed), users will get prompted several times to update. This will hold off for a bit
// of time just in case there are multiple deployments close together. If we wanted to have a "leading debounce" we'd
// need to persist the last update time... this is easier.
// But for now, disable it entirely. We can override per app/target if need be.
const DEBOUNCE_TIME_MS = 0; //2 * 60 * 60 * 1000;
const Context = createContext(null);
export function AppUpdateProvider({ updater, logger, debounce_ms, dismiss_ms, children, }) {
    const [state, setState] = useState({
        newVersionAvailableTimestamp: 0,
        dismissedTimestamp: 0,
    });
    const logRef = useRef(logger);
    useEffect(() => {
        logRef.current = logger;
    });
    useEffect(() => {
        logRef.current.debug("AppUpdateProvider", "subscribing");
        try {
            return updater.onUpdateAvailable(update => {
                logRef.current.info("AppUpdateProvider", "update state", update);
                setState(prev => (Object.assign(Object.assign(Object.assign({}, update), prev), { newVersionAvailableTimestamp: Date.now() })));
            });
        }
        catch (error) {
            logRef.current.error("AppUpdateProvider", error);
        }
    }, [updater]);
    const actions = useMemo(() => {
        return {
            dismiss() {
                return __awaiter(this, void 0, void 0, function* () {
                    logRef.current.info("AppUpdateProvider", "dismiss", state);
                    setState(prev => (Object.assign(Object.assign({}, prev), { dismissedTimestamp: Date.now() })));
                });
            },
            reload() {
                return __awaiter(this, void 0, void 0, function* () {
                    try {
                        logRef.current.info("AppUpdateProvider", "reload", state);
                        // If for some reason the app doesn't restart, at least dismiss the message
                        setState(prev => (Object.assign(Object.assign({}, prev), { dismissedTimestamp: Date.now() })));
                        yield updater.reload();
                    }
                    catch (error) {
                        logRef.current.error("AppUpdateProvider", error);
                    }
                });
            },
            checkNow() {
                return __awaiter(this, void 0, void 0, function* () {
                    var _a, _b;
                    logRef.current.info("AppUpdateProvider", "checkNow");
                    try {
                        const found = (_b = (yield ((_a = updater.checkNow) === null || _a === void 0 ? void 0 : _a.call(updater)))) !== null && _b !== void 0 ? _b : false;
                        logRef.current.info("AppUpdateProvider", "checkNow result", { found });
                        if (found) {
                            setState(prev => (Object.assign(Object.assign({}, prev), { newVersionAvailableTimestamp: Date.now(), 
                                // A manual, user-initiated check should reliably surface the prompt, even if the
                                // user dismissed an earlier nudge and its cooldown hasn't elapsed yet.
                                dismissedTimestamp: 0 })));
                        }
                        return found;
                    }
                    catch (error) {
                        logRef.current.error("AppUpdateProvider", error);
                        throw error;
                    }
                });
            },
        };
    }, [updater, state]);
    // Force a re-render every minute so the re-notify will actually appear. Yes, we could figure out exactly how many
    // ms to wait and set a timer for that. Or, we can just cause a tiny re-render which has basically zero impact.
    // Heck, change it to an hour if it's a concern!
    useTickEveryMinute();
    const shouldPrompt = shouldShowAppUpdatePrompt({
        debounce_ms: debounce_ms !== null && debounce_ms !== void 0 ? debounce_ms : DEBOUNCE_TIME_MS,
        dismiss_cooldown_ms: dismiss_ms !== null && dismiss_ms !== void 0 ? dismiss_ms : DISMISS_TIME_MS,
        now: Date.now(),
        newVersionAvailableTimestamp: state.newVersionAvailableTimestamp,
        dismissedTimestamp: state.dismissedTimestamp,
    });
    const value = useMemo(() => (Object.assign(Object.assign(Object.assign({}, state), actions), { shouldPrompt })), [state, actions, shouldPrompt]);
    return _jsx(Context.Provider, { value: value, children: children });
}
function useTickEveryMinute() {
    const [, refresh] = useState(0);
    useEffect(() => {
        const id = setInterval(() => refresh(v => v + 1), 60 * 1000);
        return () => {
            clearInterval(id);
        };
    });
    return null;
}
export function useAppUpdate() {
    const value = useContext(Context);
    if (value === null) {
        throw new Error("useAppUpdate must be used within an <AppUpdateProvider/> context");
    }
    return value;
}
